import React, { useCallback, useEffect, useState } from "react";
import { cn } from "utils/cn";

/**
 * The chat agent sidebar's hover (wt-chat-agent-prototype `beautifului/ui/GlideMenu`): one highlight floats
 * behind whichever row (`data-menu-row`) is hovered or focused and glides between them. It sits below the
 * rows, so rows are `relative z-10`.
 */

interface GlideMenuProps {
  className?: string;
  /** classes for the floating highlight (e.g. "inset-x-0 rounded-control bg-hover") */
  highlightClassName?: string;
  children: React.ReactNode;
}

const findRow = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof HTMLElement)) return null;

  return target.closest("[data-menu-row]");
};

/**
 * The one highlight on screen: the rail has a GlideMenu per section and the flyout its own, and a missed
 * `mouseleave` (a fast move, the flyout opening over the pointer's path) used to leave a second row lit.
 * Whichever list lights a row puts the previous one out.
 */
let putOutLit: (() => void) | null = null;

const GlideMenu = ({ className, highlightClassName, children }: GlideMenuProps) => {
  const [highlight, setHighlight] = useState<{ top: number; height: number; visible: boolean }>({ top: 0, height: 0, visible: false });

  const hide = useCallback(() => setHighlight((current) => (current.visible ? { ...current, visible: false } : current)), []);

  const moveTo = (target: EventTarget | null) => {
    const row = findRow(target);
    if (!row) return;
    if (putOutLit !== hide) putOutLit?.();
    putOutLit = hide;
    setHighlight({ top: row.offsetTop, height: row.offsetHeight, visible: true });
  };

  useEffect(
    () => () => {
      if (putOutLit === hide) putOutLit = null;
    },
    [hide]
  );

  return (
    <div className={cn("relative", className)} onMouseOver={(e) => moveTo(e.target)} onFocus={(e) => moveTo(e.target)} onMouseLeave={hide}>
      <span
        aria-hidden
        className={cn("pointer-events-none absolute z-0", highlightClassName)}
        style={{
          top: highlight.top,
          height: highlight.height,
          opacity: highlight.visible ? 1 : 0,
          transition: "top 220ms cubic-bezier(0.22,1,0.36,1), height 220ms cubic-bezier(0.22,1,0.36,1), opacity 150ms ease-out",
        }}
      />
      {children}
    </div>
  );
};

export default GlideMenu;
